import { FC, useCallback, useState } from "react";
import {
  selectBookings,
  setBookings,
} from "application/features/bookings/bookingsSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Heading from "shared/Heading/Heading";
import { ArrowRightIcon, TrashIcon } from "@heroicons/react/24/outline";
import StayCard from "components/StayCard/StayCard";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import {
  BookingDataType,
  isExperience,
  isHolidayPackage,
  isStay,
} from "data/interfaces";
import { useTranslation } from "react-i18next";
import HolidayPackageCartRow from "./HolidayPackageCartRow";
import OnlyInPackageStayGuard from "./OnlyInPackageStayGuard";
import { useCartRequestHandlers } from "application/hooks/useCartRequestHandlers";

interface MyTripPageProps {}

const MyTripPage: FC<MyTripPageProps> = (props) => {
  const { t, ready } = useTranslation("translation");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const bookings: BookingDataType[] = useSelector(selectBookings);

  const handleRemove = (item_id: string | number) => {
    // remove from bookings list by item_id
    const newBookings: BookingDataType[] = bookings.filter(
      (booking: BookingDataType) => booking.id !== item_id,
    );
    // update bookings list
    dispatch(setBookings(newBookings));
  };

  const {
    handleRemoveFromTrip,
    handleCartSendRequest,
    handleChangeDates,
    isSendingRequest,
  } = useCartRequestHandlers();

  const handleCalculateTotal = () => {
    let total = 0;
    bookings.forEach((booking: BookingDataType) => {
      total += Number(booking.price);
    });
    return total;
  };

  // Track which cart items are currently within the latest_booking window,
  // using the LIVE server value (populated by HolidayPackageCartRow on mount).
  // Falls back to the snapshot if the live fetch is still in flight.
  const [problematicIds, setProblematicIds] = useState<Set<string | number>>(
    () => new Set(),
  );
  const [onlyInPackageStayIds, setOnlyInPackageStayIds] = useState<Set<string | number>>(
    () => new Set(),
  );

  const handleProblematicChange = useCallback(
    (id: string | number, isProblematic: boolean) => {
      setProblematicIds((prev) => {
        const next = new Set(prev);
        if (isProblematic) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [],
  );

  const handleOnlyInPackageStayChange = useCallback(
    (id: string | number, isProblematic: boolean) => {
      setOnlyInPackageStayIds((prev) => {
        const next = new Set(prev);
        if (isProblematic) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [],
  );

  const problematic = bookings.filter((b) => problematicIds.has(b.id));
  const showCartValidationBanner = problematic.length > 0;

  const handleContinue = () => {
    if (problematic.length > 0) {
      const el = document.getElementById(`cart-item-${problematic[0].id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (onlyInPackageStayIds.size > 0) {
      const firstId = Array.from(onlyInPackageStayIds)[0];
      const el = document.getElementById(`cart-item-${firstId}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    navigate("/client/booking/calendar");
  };

  const total = handleCalculateTotal();

  if (!ready) return null;

  return (
    <div>
      <div className="container relative pt-5 pb-16 lg:pb-20 lg:pt-8 flex flex-col gap-4">
        <Heading desc={t("translation:myTrip.description")} className="m-0">
          {t("translation:myTrip.title")}
        </Heading>

        {bookings.map((booking: BookingDataType) => {
          let cover: string = "";
          let images: string[] = [];
          let uuid: string | number = "";
          let href: string = "";
          let address: string = "";
          let annotation: string = "";
          if (isStay(booking.item)) {
            uuid = booking.item.property_uuid;
            cover = booking.item.cover;
            images =
              booking.item.gallery_images && booking.item.gallery_images.length
                ? booking.item.gallery_images
                : cover
                  ? [cover]
                  : [];
            href = `/stay/${uuid}`;
            annotation = `${booking.item.property_type}`;
            address = booking.item.address;
          } else if (isExperience(booking.item)) {
            uuid = booking.item.experience_uuid;
            cover = booking.item.cover;
            images =
              booking.item.gallery_images && booking.item.gallery_images.length
                ? booking.item.gallery_images
                : [];
            href = `/experience/${uuid}`;
            annotation = `${booking.item.type}`;
            address = booking.item.city;
          } else if (isHolidayPackage(booking.item)) {
            return (
              <HolidayPackageCartRow
                key={booking.id}
                booking={booking}
                onRemoveById={handleRemove}
                onRemoveByItem={handleRemoveFromTrip}
                onSendRequest={handleCartSendRequest}
                onChangeDates={handleChangeDates}
                isSendingRequest={isSendingRequest}
                onProblematicChange={handleProblematicChange}
              />
            );
          } else {
            return null;
          }

          return (
            <div
              key={booking.id}
              id={`cart-item-${booking.id}`}
              className="mb-4"
            >
              <MultiStockCard
                id={uuid}
                cover={cover}
                annotation={annotation}
                images={images}
                title={booking.item.name}
                address={address}
                mood={booking.item.mood}
                // rating={booking.item.rating}
                // reviewCount={booking.item.reviews_count}
                price={
                  Number(booking.item.price || 0) +
                  Number(booking.additional_fees || 0)
                }
                href={href}
                loading={false}
              >
                <span
                  className="flex flex-row items-center justify-center text-red-500 gap-2 bg-neutral-100 dark:bg-neutral-800 dark:text-neutral-200 py-2 px-2 rounded-full cursor-pointer hover:opacity-80 w-[8rem]"
                  onClick={() => handleRemove(booking.id)}
                >
                  <TrashIcon className="h-5 w-5" />
                  <span>{t("translation:myTrip.actions.remove")}</span>
                </span>
              </MultiStockCard>
              <OnlyInPackageStayGuard
                booking={booking}
                onRemove={(item) => handleRemoveFromTrip(item)}
                onProblematicChange={handleOnlyInPackageStayChange}
              />
            </div>
          );
        })}

        {bookings.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-4">
            <span>{t("translation:myTrip.emptyBasket")}</span>
            <ButtonPrimary onClick={() => (window.location.href = "/")}>
              {t("translation:myTrip.actions.explore")}
            </ButtonPrimary>
          </div>
        )}
        {showCartValidationBanner && (
          <div
            role="alert"
            className="mb-4 p-4 rounded-lg border border-red-300 bg-red-50 text-red-800"
          >
            {t("translation:detailPages.common.cartValidationBanner", {
              count: problematic.length,
            })}
          </div>
        )}
        {onlyInPackageStayIds.size > 0 && (
          <div
            role="alert"
            className="mb-4 p-4 rounded-lg border border-red-300 bg-red-50 text-red-800"
          >
            Alcune strutture sono prenotabili solo all'interno di un pacchetto. Rimuovile per continuare.
          </div>
        )}

        {bookings.length > 0 && (
          <div className="fixed bottom-[4rem] px-5 right-0 w-full flex justify-end z-40">
            <div className="flex flex-row items-center">
              <ButtonPrimary onClick={() => handleContinue()}>
                {t("translation:myTrip.actions.continue")}
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </ButtonPrimary>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTripPage;
